import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  MessageSquare,
  Send,
  Car,
  Search,
  Sparkles,
  ExternalLink,
  Loader2,
  CheckCheck,
  CheckCircle2,
  Star,
} from 'lucide-react'
import { toast } from 'sonner'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { SectionHeading } from '@/components/common'
import { useAuth } from '@/providers/auth-provider'
import { appRoutes } from '@/constants'
import {
  useConversations,
  useMessages,
  useSendMessage,
  useMarkAsRead,
  useMarkDealDone,
} from '../hooks/use-chat'
import { useMyReviews, useSubmitDealReview } from '@/modules/reviews/hooks/use-reviews'

export function MessagesPage() {
  const { user } = useAuth()
  const { data: conversations = [], isLoading: isLoadingConversations } = useConversations()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [messageInput, setMessageInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const sendMessage = useSendMessage()
  const markAsRead = useMarkAsRead()
  const markDealDone = useMarkDealDone()
  const { data: myReviews = [] } = useMyReviews()
  const submitDealReview = useSubmitDealReview()

  const [dealReviewModalOpen, setDealReviewModalOpen] = useState(false)
  const [rating, setRating] = useState(5)
  const [hoverRating, setHoverRating] = useState(0)
  const [comment, setComment] = useState('')
  const [confirmDealDoneId, setConfirmDealDoneId] = useState<number | null>(null)

  const getDealReview = (conversationId: number) =>
    myReviews.find((r: any) => r.reviewType === 'CAR_DEAL' && Number(r.referenceId) === Number(conversationId))

  // Auto-select first conversation
  useEffect(() => {
    if (!selectedId && conversations.length > 0) {
      setSelectedId(conversations[0].id)
    }
  }, [conversations, selectedId])

  // Mark as read when conversation is opened
  useEffect(() => {
    if (selectedId) {
      markAsRead.mutate(selectedId)
    }
  }, [selectedId])

  const selectedConversation = conversations.find((c) => c.id === selectedId) || null
  const { data: messages = [], isLoading: isLoadingMessages } = useMessages(selectedId)

  // Scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  const filteredConversations = conversations.filter((c) => {
    const otherUser = c.buyerId === user?.id ? c.seller : c.buyer
    const otherName = `${otherUser.firstName} ${otherUser.lastName}`.toLowerCase()
    const carTitle = c.carListing?.title?.toLowerCase() || ''
    const q = searchTerm.toLowerCase()
    return otherName.includes(q) || carTitle.includes(q)
  })

  const handleSend = () => {
    if (!messageInput.trim() || !selectedId) return
    sendMessage.mutate(
      {
        conversationId: selectedId,
        content: messageInput.trim(),
      },
      {
        onSuccess: () => {
          setMessageInput('')
        },
      },
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="space-y-6">
      <SectionHeading
        eyebrow="Communication Hub"
        title="Inquiries & Messages"
        description="Direct real-time conversations regarding verified cars, inspections, and pricing discussions."
        align="left"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[720px] rounded-3xl border border-border/80 bg-card/60 backdrop-blur-xl overflow-hidden shadow-xl">
        {/* Left Pane: Conversations List (4 columns) */}
        <div className="lg:col-span-4 xl:col-span-4 border-r border-border/60 flex flex-col h-full bg-background/40">
          <div className="p-4 border-b border-border/60 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-base text-foreground flex items-center gap-2">
                <MessageSquare className="size-4 text-primary" /> Active Chats
              </h3>
              <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                {conversations.length} total
              </Badge>
            </div>
            <div className="relative">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by vehicle or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-muted/40 border-border/60 text-xs h-9 rounded-xl"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {isLoadingConversations ? (
              <div className="h-48 flex items-center justify-center text-muted-foreground text-xs gap-2">
                <Loader2 className="size-4 animate-spin text-primary" /> Loading chats...
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="h-64 flex flex-col items-center justify-center text-center p-6 text-muted-foreground space-y-2">
                <MessageSquare className="size-8 opacity-40 text-primary" />
                <p className="text-xs font-semibold">No inquiries found</p>
                <p className="text-[11px] max-w-xs">
                  When customers or dealers inquire about vehicle listings, conversations will appear here.
                </p>
              </div>
            ) : (
              filteredConversations.map((conv) => {
                const isBuyer = conv.buyerId === user?.id
                const otherUser = isBuyer ? conv.seller : conv.buyer
                const otherName = `${otherUser.firstName} ${otherUser.lastName}`
                const isSelected = conv.id === selectedId

                // Car Image
                let carImage = 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=200&q=80'
                if (conv.carListing?.images && Array.isArray(conv.carListing.images) && conv.carListing.images.length > 0) {
                  const first = conv.carListing.images[0]
                  carImage = typeof first === 'string' ? first : first.url || carImage
                }

                return (
                  <button
                    key={conv.id}
                    type="button"
                    onClick={() => setSelectedId(conv.id)}
                    className={`w-full text-left p-3 rounded-2xl transition-all duration-150 flex items-start gap-3 border ${
                      isSelected
                        ? 'bg-primary/10 border-primary/40 shadow-xs'
                        : 'bg-transparent border-transparent hover:bg-muted/50'
                    }`}
                  >
                    <div className="relative shrink-0">
                      <img
                        src={carImage}
                        alt={conv.carListing?.title || 'Car'}
                        className="size-12 rounded-xl object-cover border border-border"
                      />
                      {conv.unreadCount && conv.unreadCount > 0 ? (
                        <span className="absolute -top-1 -right-1 size-5 bg-primary text-primary-foreground font-bold text-[10px] rounded-full flex items-center justify-center border-2 border-background">
                          {conv.unreadCount}
                        </span>
                      ) : null}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-1">
                        <p className="font-bold text-xs text-foreground truncate">{otherName}</p>
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {new Date(conv.lastMessageAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-1 mt-0.5">
                        <p className="text-[11px] font-medium text-primary truncate">
                          {conv.carListing?.title || 'Vehicle Inquiry'}
                        </p>
                        {conv.dealStatus === 'DEAL_DONE' && (
                          <Badge className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 text-[9px] py-0 px-1 font-bold shrink-0">
                            Deal Done
                          </Badge>
                        )}
                      </div>

                      <p className="text-[11px] text-muted-foreground truncate mt-1">
                        {conv.lastMessage?.content || 'No messages yet'}
                      </p>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* Right Pane: Message Thread (8 columns) */}
        <div className="lg:col-span-8 xl:col-span-8 flex flex-col h-full bg-card/30">
          {selectedConversation ? (
            <>
              {/* Thread Header */}
              {(() => {
                const isBuyer = selectedConversation.buyerId === user?.id
                const otherUser = isBuyer ? selectedConversation.seller : selectedConversation.buyer
                const otherName = `${otherUser.firstName} ${otherUser.lastName}`
                const roleBadge = otherUser.role === 'DEALER' ? 'Verified Dealer' : otherUser.role === 'SELLER' ? 'Private Seller' : 'Verified Buyer'
                const isDealDone = selectedConversation.dealStatus === 'DEAL_DONE'
                const dealReview = getDealReview(selectedConversation.id)

                return (
                  <div>
                    <div className="p-4 border-b border-border/60 bg-background/50 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="size-11 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center font-bold text-primary font-display text-sm shrink-0">
                          {otherUser.firstName?.[0] || 'U'}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h4 className="font-display text-sm font-bold text-foreground truncate">{otherName}</h4>
                            <Badge variant="outline" className="text-[10px] py-0 border-emerald-500/30 text-emerald-500">
                              {roleBadge}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            Inquiry regarding: <span className="font-semibold text-foreground">{selectedConversation.carListing?.title || 'Vehicle'}</span>
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {isDealDone && (
                          <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 text-xs font-bold gap-1 px-2.5 py-1">
                            <CheckCircle2 className="size-3 text-emerald-500" /> Deal Done
                          </Badge>
                        )}

                        {/* Dealer / Seller action: Mark Deal as Done */}
                        {!isBuyer && !isDealDone && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs font-bold border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/10 gap-1.5 rounded-xl h-8"
                            onClick={() => setConfirmDealDoneId(selectedConversation.id)}
                          >
                            <CheckCircle2 className="size-3.5 text-emerald-500" /> Mark Deal as Done
                          </Button>
                        )}

                        {/* Buyer action: Review Dealership / Service once deal is marked done */}
                        {isBuyer && isDealDone && (
                          dealReview ? (
                            <span className="flex items-center gap-1.5 text-xs font-bold text-amber-500 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-xl">
                              <Star className="size-3.5 fill-amber-500 text-amber-500" /> Service Rated {dealReview.rating}★
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs gap-1.5 rounded-xl h-8 shadow-xs"
                              onClick={() => {
                                setRating(5)
                                setComment('')
                                setDealReviewModalOpen(true)
                              }}
                            >
                              <Star className="size-3.5 fill-white" /> Review Dealer Service
                            </Button>
                          )
                        )}

                        {selectedConversation.carListing && (
                          <Link
                            to={appRoutes.carDetails(selectedConversation.carListing.slug)}
                            className="shrink-0"
                          >
                            <Button size="sm" variant="outline" className="text-xs gap-1.5 border-border rounded-xl h-8">
                              <Car className="size-3.5 text-primary" /> View Car Page <ExternalLink className="size-3" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>

                    {/* Deal Done Status Announcement Banner */}
                    {isDealDone && (
                      <div className="bg-emerald-500/10 border-b border-emerald-500/20 px-5 py-2.5 flex items-center justify-between gap-3 text-xs">
                        <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-medium">
                          <CheckCircle2 className="size-4 shrink-0 text-emerald-500" />
                          <span>Deal finalized &amp; marked complete by respective dealer/seller.</span>
                        </div>
                        {isBuyer && !dealReview && (
                          <button
                            type="button"
                            onClick={() => {
                              setRating(5)
                              setComment('')
                              setDealReviewModalOpen(true)
                            }}
                            className="font-bold text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1 shrink-0"
                          >
                            <Star className="size-3.5 fill-amber-500 text-amber-500" /> Rate Service &rarr;
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )
              })()}

              {/* Messages Body */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {isLoadingMessages ? (
                  <div className="h-full flex items-center justify-center text-xs text-muted-foreground gap-2">
                    <Loader2 className="size-4 animate-spin text-primary" /> Loading conversation...
                  </div>
                ) : messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground p-6 space-y-2">
                    <Sparkles className="size-8 text-primary" />
                    <p className="font-semibold text-sm text-foreground">Say hello!</p>
                    <p className="text-xs max-w-sm">
                      Send a message below to discuss pricing, vehicle condition, and test drive schedules.
                    </p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isMe = msg.senderId === user?.id
                    return (
                      <div
                        key={msg.id}
                        className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
                      >
                        <div
                          className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-xs leading-relaxed ${
                            isMe
                              ? 'bg-primary text-primary-foreground rounded-br-xs font-medium shadow-sm'
                              : 'bg-muted/80 text-foreground border border-border/60 rounded-bl-xs'
                          }`}
                        >
                          {msg.content}
                        </div>
                        <span className="text-[10px] text-muted-foreground mt-1 px-1 flex items-center gap-1">
                          {new Date(msg.createdAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {isMe && <CheckCheck className="size-3 text-primary" />}
                        </span>
                      </div>
                    )
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Send Bar */}
              <div className="p-4 border-t border-border/60 bg-background/60 flex items-center gap-3">
                <Input
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Write your reply..."
                  className="bg-muted/60 border-border text-xs rounded-2xl h-11"
                />
                <Button
                  onClick={handleSend}
                  disabled={!messageInput.trim() || sendMessage.isPending}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-2xl px-5 h-11 font-bold gap-2 shrink-0 glow-md"
                >
                  {sendMessage.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <>
                      <span>Send</span>
                      <Send className="size-3.5" />
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center p-8 text-muted-foreground space-y-3">
              <MessageSquare className="size-12 text-primary opacity-30" />
              <h3 className="font-display font-bold text-lg text-foreground">Select a conversation</h3>
              <p className="text-xs max-w-sm">
                Choose a conversation from the left to view customer inquiries or send replies directly.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Confirm Deal Done Modal for Dealer/Seller */}
      <Dialog open={Boolean(confirmDealDoneId)} onOpenChange={() => setConfirmDealDoneId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-bold flex items-center gap-2">
              <CheckCircle2 className="size-5 text-emerald-500" />
              Mark Deal as Completed?
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2 text-xs">
            <p className="text-muted-foreground leading-relaxed">
              Confirm that this car deal/purchase transaction is complete. Once marked as{' '}
              <strong className="text-foreground">DEAL_DONE</strong>:
            </p>

            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>The conversation deal status will officially be updated to <strong>DEAL_DONE</strong>.</li>
              <li>A verification notice will be logged in this chat.</li>
              <li>The buyer will be prompted to leave a verified dealership &amp; service review.</li>
            </ul>

            <div className="flex items-center justify-end gap-3 pt-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmDealDoneId(null)}
                disabled={markDealDone.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-1.5"
                disabled={markDealDone.isPending}
                onClick={() => {
                  if (!confirmDealDoneId) return
                  markDealDone.mutate(confirmDealDoneId, {
                    onSuccess: () => {
                      toast.success('Deal marked as completed! (DEAL_DONE)')
                      setConfirmDealDoneId(null)
                    },
                    onError: (err: any) => {
                      toast.error(err?.message || 'Failed to update deal status')
                    },
                  })
                }}
              >
                {markDealDone.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin mr-1" /> Updating...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="size-4" /> Confirm Deal Done
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Buyer Service Review Modal */}
      <Dialog open={dealReviewModalOpen} onOpenChange={setDealReviewModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold flex items-center gap-2">
              <Star className="size-5 fill-amber-500 text-amber-500" />
              Dealership &amp; Service Review
            </DialogTitle>
          </DialogHeader>

          {selectedConversation && (
            <div className="space-y-4 pt-2">
              <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-500 flex items-center gap-1">
                  <CheckCircle2 className="size-3" /> Verified Car Deal Completed
                </span>
                <p className="text-sm font-bold text-foreground">
                  {selectedConversation.carListing?.title || 'Vehicle Deal'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Dealer / Seller:{' '}
                  <strong className="text-foreground">
                    {selectedConversation.seller?.firstName} {selectedConversation.seller?.lastName}
                  </strong>
                </p>
              </div>

              {/* Star Rating */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-foreground">
                  Rate Dealer Service Experience <span className="text-destructive">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className="p-1 transition-transform hover:scale-110 focus:outline-none"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                    >
                      <Star
                        className={`size-7 transition-colors ${
                          (hoverRating || rating) >= star
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-muted-foreground/30'
                        }`}
                      />
                    </button>
                  ))}
                  <span className="text-sm font-bold text-amber-500 ml-2">
                    {rating} / 5 Stars
                  </span>
                </div>
              </div>

              {/* Comments */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-foreground">
                  Your Dealership Feedback &amp; Service Review <span className="text-destructive">*</span>
                </label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Share details regarding dealer responsiveness, transparency, inspection report accuracy, and overall car delivery handover..."
                  className="min-h-28 text-xs"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setDealReviewModalOpen(false)}
                  disabled={submitDealReview.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-primary hover:bg-primary/90 text-white font-bold text-xs"
                  disabled={!comment.trim() || submitDealReview.isPending}
                  onClick={() => {
                    submitDealReview.mutate(
                      {
                        conversationId: selectedConversation.id,
                        rating,
                        comment: comment.trim(),
                      },
                      {
                        onSuccess: () => {
                          toast.success('Thank you! Your dealership service review has been published.')
                          setDealReviewModalOpen(false)
                        },
                        onError: (err: any) => {
                          toast.error(err?.message || 'Failed to submit review')
                        },
                      },
                    )
                  }}
                >
                  {submitDealReview.isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin mr-2" /> Submitting...
                    </>
                  ) : (
                    'Submit Review'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
